import { supabase } from '../supabaseClient';
import axios from 'axios';
import { apiEndpoints, API_BASE_URL } from '../config/api';

export interface FreightRecord {
    id?: string;
    // Legacy string fields (preserved for OCR parsing and backward compatibility)
    outlet: string | null;
    carrier: string | null;
    supplier: string | null;

    // New multi-company structure - role-specific company foreign keys
    invoicerCompanyId?: string | null;    // Company that sent the invoice (selected during upload)
    carrierCompanyId?: string | null;     // Transportation company
    shipperCompanyId?: string | null;     // Company sending goods (supplier)
    consigneeCompanyId?: string | null;   // Company receiving goods (outlet)
    billToCompanyId?: string | null;      // Company responsible for payment

    // Other freight data
    deliveryDate: string | null;
    deliveryMonth: string | null;
    ticketNumber: string | null;
    bolNumber: string | null;
    netTons: number | null;
    grossPrice: number | null;
    freightCost: number | null;
    netGrossPrice: number | null;
    driver: string | null;
    dustCost: number | null;
    paidStatus: boolean;
    confidenceScore: number;
    needsReview: boolean;
    vendorType: string;
    createdAt?: string;
    updatedAt?: string;
    emailId?: string | null;
    attachmentUrls?: string[];
    rawOcrData?: any[];
    unmatchedCompanies?: Array<{
        extractedName: string;
        role: 'carrier' | 'supplier' | 'outlet';
        field: string;
        suggestions?: Array<{ id: string; name: string; types: string[] }>;
    }>;
}

export async function parseFreightDocument(
    ocrText: string,
    rawOcrData?: any,
    attachmentUrls?: string[],
    invoicerCompany?: any
): Promise<{
    parsedData: FreightRecord;
    savedRecord?: FreightRecord;
    error?: string;
    unmatchedCompanies?: Array<{
        extractedName: string;
        role: 'carrier' | 'supplier' | 'outlet';
        field: string;
        suggestions?: Array<{ id: string; name: string; types: string[] }>;
    }>;
}> {
    try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();

        const response = await axios.post(apiEndpoints.parseFreight, {
            ocrText,
            userId: user?.id,
            rawOcrData,
            attachmentUrls,
            invoicerCompany,
        });

        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.error || 'Failed to parse document');
    }
}

export async function getFreightRecords(impersonationData?: {
    isImpersonating: boolean;
    impersonatedUserId: string;
    accountId: string;
}): Promise<FreightRecord[]> {
    try {
        let recordsData: any[] = [];

        if (impersonationData?.isImpersonating) {
            // Use impersonation endpoint for freight records
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            const token = (await supabase.auth.getSession()).data.session?.access_token;

            const response = await fetch(`${API_BASE_URL}/admin/impersonate/${impersonationData.impersonatedUserId}/account/${impersonationData.accountId}/freight-records?currentUserId=${currentUser?.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch impersonated freight records');
            }

            const responseData = await response.json();
            recordsData = responseData.freightRecords || [];
        } else {
            // Normal freight records fetching
            const { data, error } = await supabase
                .from('freight_records')
                .select(`
                    id,
                    outlet,
                    carrier,
                    supplier,
                    invoicer_company_id,
                    carrier_company_id,
                    shipper_company_id,
                    consignee_company_id,
                    bill_to_company_id,
                    delivery_date,
                    delivery_month,
                    ticket_number,
                    bol_number,
                    net_tons,
                    gross_price,
                    freight_cost,
                    net_gross_price,
                    driver,
                    dust_cost,
                    paid_status,
                    confidence_score,
                    needs_review,
                    vendor_type,
                    created_at,
                    updated_at,
                    email_id,
                    attachment_urls,
                    raw_ocr_data
                `)
                .order('created_at', { ascending: false });

            if (error) {
                throw new Error(error.message);
            }

            recordsData = data || [];
        }

        // Transform database fields to match FreightRecord interface
        const transformedData = recordsData.map((record: any) => ({
            id: record.id,
            // Legacy string fields
            outlet: record.outlet,
            carrier: record.carrier,
            supplier: record.supplier,

            // New multi-company structure
            invoicerCompanyId: record.invoicer_company_id,
            carrierCompanyId: record.carrier_company_id,
            shipperCompanyId: record.shipper_company_id,
            consigneeCompanyId: record.consignee_company_id,
            billToCompanyId: record.bill_to_company_id,

            // Other freight data
            deliveryDate: record.delivery_date,
            deliveryMonth: record.delivery_month,
            ticketNumber: record.ticket_number,
            bolNumber: record.bol_number,
            netTons: record.net_tons,
            grossPrice: record.gross_price,
            freightCost: record.freight_cost,
            netGrossPrice: record.net_gross_price,
            driver: record.driver,
            dustCost: record.dust_cost,
            paidStatus: record.paid_status,
            confidenceScore: record.confidence_score,
            needsReview: record.needs_review,
            vendorType: record.vendor_type,
            createdAt: record.created_at,
            updatedAt: record.updated_at,
            emailId: record.email_id,
            attachmentUrls: record.attachment_urls,
            rawOcrData: record.raw_ocr_data
        }));

        return transformedData;
    } catch (error: any) {
        throw new Error(error.message || 'Failed to fetch records');
    }
}

export async function getFreightRecord(
    id: string,
    impersonationData?: {
        isImpersonating: boolean;
        impersonatedUserId: string;
        accountId: string;
    }
): Promise<FreightRecord> {
    try {
        let recordData: any = null;

        if (impersonationData?.isImpersonating) {
            // Use impersonation endpoint for single freight record
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            const token = (await supabase.auth.getSession()).data.session?.access_token;

            const response = await fetch(`${API_BASE_URL}/admin/impersonate/${impersonationData.impersonatedUserId}/account/${impersonationData.accountId}/freight-records/${id}?currentUserId=${currentUser?.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch impersonated freight record');
            }

            const responseData = await response.json();
            recordData = responseData.freightRecord;
        } else {
            // Normal freight record fetching
            const { data, error } = await supabase
                .from('freight_records')
                .select(`
                    id,
                    outlet,
                    carrier,
                    supplier,
                    invoicer_company_id,
                    carrier_company_id,
                    shipper_company_id,
                    consignee_company_id,
                    bill_to_company_id,
                    delivery_date,
                    delivery_month,
                    ticket_number,
                    bol_number,
                    net_tons,
                    gross_price,
                    freight_cost,
                    net_gross_price,
                    driver,
                    dust_cost,
                    paid_status,
                    confidence_score,
                    needs_review,
                    vendor_type,
                    created_at,
                    updated_at,
                    email_id,
                    attachment_urls,
                    raw_ocr_data
                `)
                .eq('id', id)
                .single();

            if (error) {
                throw new Error(error.message);
            }

            if (!data) {
                throw new Error('Record not found');
            }

            recordData = data;
        }

        // Transform database fields to match FreightRecord interface
        return {
            id: recordData.id,
            // Legacy string fields
            outlet: recordData.outlet,
            carrier: recordData.carrier,
            supplier: recordData.supplier,

            // New multi-company structure
            invoicerCompanyId: recordData.invoicer_company_id,
            carrierCompanyId: recordData.carrier_company_id,
            shipperCompanyId: recordData.shipper_company_id,
            consigneeCompanyId: recordData.consignee_company_id,
            billToCompanyId: recordData.bill_to_company_id,

            // Other freight data
            deliveryDate: recordData.delivery_date,
            deliveryMonth: recordData.delivery_month,
            ticketNumber: recordData.ticket_number,
            bolNumber: recordData.bol_number,
            netTons: recordData.net_tons,
            grossPrice: recordData.gross_price,
            freightCost: recordData.freight_cost,
            netGrossPrice: recordData.net_gross_price,
            driver: recordData.driver,
            dustCost: recordData.dust_cost,
            paidStatus: recordData.paid_status,
            confidenceScore: recordData.confidence_score,
            needsReview: recordData.needs_review,
            vendorType: recordData.vendor_type,
            createdAt: recordData.created_at,
            updatedAt: recordData.updated_at,
            emailId: recordData.email_id,
            attachmentUrls: recordData.attachment_urls,
            rawOcrData: recordData.raw_ocr_data
        };
    } catch (error: any) {
        throw new Error(error.message || 'Failed to fetch record');
    }
}

export async function deleteFreightRecord(
    id: string,
    impersonationData?: {
        isImpersonating: boolean;
        impersonatedUserId: string;
        accountId: string;
    }
): Promise<void> {
    try {
        if (impersonationData?.isImpersonating) {
            // Use impersonation endpoint for delete
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            const token = (await supabase.auth.getSession()).data.session?.access_token;

            const response = await fetch(`${API_BASE_URL}/admin/impersonate/${impersonationData.impersonatedUserId}/account/${impersonationData.accountId}/freight-records/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ currentUserId: currentUser?.id }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete impersonated freight record');
            }
        } else {
            // Normal delete
            const { error } = await supabase
                .from('freight_records')
                .delete()
                .eq('id', id);

            if (error) {
                throw new Error(error.message);
            }
        }
    } catch (error: any) {
        throw new Error(error.message || 'Failed to delete record');
    }
}

export async function updateFreightRecord(
    id: string,
    updates: Partial<FreightRecord>,
    impersonationData?: {
        isImpersonating: boolean;
        impersonatedUserId: string;
        accountId: string;
    }
): Promise<FreightRecord | null> {
    try {
        const now = new Date().toISOString();
        const mappedData = {
            outlet: updates.outlet,
            carrier: updates.carrier,
            supplier: updates.supplier,

            invoicer_company_id: updates.invoicerCompanyId || null,
            carrier_company_id: updates.carrierCompanyId || null,
            shipper_company_id: updates.shipperCompanyId || null,
            consignee_company_id: updates.consigneeCompanyId || null,
            bill_to_company_id: updates.billToCompanyId || null,

            delivery_date: updates.deliveryDate?.toString?.() || null,
            delivery_month: updates.deliveryMonth,
            ticket_number: updates.ticketNumber,
            bol_number: updates.bolNumber,
            net_tons: updates.netTons,
            gross_price: updates.grossPrice,
            freight_cost: updates.freightCost,
            net_gross_price: updates.netGrossPrice,
            driver: updates.driver,
            dust_cost: updates.dustCost,
            paid_status: updates.paidStatus,
            confidence_score: updates.confidenceScore,
            needs_review: updates.needsReview,
            vendor_type: updates.vendorType,
            updated_at: now,
        };

        if (impersonationData?.isImpersonating) {
            // Use impersonation endpoint for update
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            const token = (await supabase.auth.getSession()).data.session?.access_token;

            const response = await fetch(`${API_BASE_URL}/admin/impersonate/${impersonationData.impersonatedUserId}/account/${impersonationData.accountId}/freight-records/${id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    currentUserId: currentUser?.id,
                    updates: mappedData
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update impersonated freight record');
            }

            const responseData = await response.json();
            return responseData.freightRecord;
        } else {
            // Normal update
            const { data, error } = await supabase
                .from('freight_records')
                .update(mappedData)
                .eq('id', id);

            if (error) {
                throw new Error(error.message);
            }

            return data;
        }
    } catch (error: any) {
        throw new Error(error.message || 'Failed to update record');
    }
} 